import { useState } from 'react'
import dayjs from 'dayjs'

import Styles from './AddStrategyModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { BrandPlan, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { ContentCategory, ContentTypeCounts, DEFAULT_CONTENT_TYPE_COUNTS } from 'utils/brandPlans/contentCategories'
import { CustomizeTable } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/CustomizeTable/CustomizeTable'
import { ContentTypeGrid } from './ContentTypeGrid'
import { Strategy } from 'screens/VirtualMarketerOnboarding/StrategiesSelection/utils'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { useLaunchStrategy } from 'hooks/queries/brandPlans/useLaunchStrategy'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useContentPlanSchedules } from 'screens/ContentPlan/useContentPlanSchedules'
import {
  deriveInitialCountsFromStrategy,
  buildContentsFromCounts,
} from 'utils/brandPlans/contentTypeDefaultsUtils'

export const AddStrategyModal = ({ brandPlan, strategy }: { strategy: Strategy; brandPlan: BrandPlan }) => {
  const { refetch: refetchBrandPlan } = useCurrentBrandPlan()
  const { refetch: refetchSchedules } = useContentPlanSchedules({ startDate: dayjs().format('YYYY-MM-DD') })
  const { closeModal, closeAllModals } = useModals()
  const useContentTypes = brandPlan.post_content_types

  const [newStrategy, setNewStrategy] = useState(strategy)
  const [counts, setCounts] = useState<ContentTypeCounts>(() => ({
    ...DEFAULT_CONTENT_TYPE_COUNTS,
    ...deriveInitialCountsFromStrategy(strategy),
  }))
  const [selectedBlogPlatform, setSelectedBlogPlatform] = useState<SelectedBlogPlatform>(
    ExternalIntegrationEnum.WordPress,
  )
  const [selectedEmailPlatform, setSelectedEmailPlatform] = useState<SelectedEmailPlatform>(
    ExternalIntegrationEnum.Mailchimp,
  )
  const { mutate: launchStrategyMutation, isLoading: isLaunching } = useLaunchStrategy({ brandPlanId: brandPlan.id })
  const isStrictBrandControl = brandPlan.brand_kit?.strict_brand_control ?? false

  const totalSocialCount = newStrategy.crosspost_contents?.[0]?.count ?? 0
  const totalBlogCount = newStrategy.seo_contents?.[0]?.count ?? 0
  const totalEmailCount = newStrategy.email_contents?.[0]?.count ?? 0
  const totalShortFormVideoCount = newStrategy.video_crosspost_contents?.[0]?.count ?? 0

  const credits = useCreditsToGenerateContent(
    useContentTypes ? 0 : totalSocialCount,
    useContentTypes ? counts.blog_post : totalBlogCount,
    useContentTypes ? counts.newsletter : totalEmailCount,
    useContentTypes ? counts.short_form_video : totalShortFormVideoCount,
    useContentTypes ? counts.still_image_post : 0,
    useContentTypes ? counts.feed_video_post : 0,
    useContentTypes ? counts.carousel_post : 0,
    useContentTypes ? counts.stories : 0,
    {
      applyVisualStyle: true,
      percentageOfVisualStylesGeneration: 1,
    },
  )

  const handleCountChange = (category: ContentCategory, delta: number) => {
    setCounts((prev) => ({
      ...prev,
      [category]: Math.max(0, prev[category] + delta),
    }))
  }

  const launchStrategy = () => {
    if (useContentTypes) {
      const contents = buildContentsFromCounts(counts)
      launchStrategyMutation(
        {
          content_strategy_type: strategy.id,
          length: newStrategy.length,
          ...contents,
          tactics: newStrategy.tactics,
        },
        {
          onSuccess: async () => {
            await refetchBrandPlan()
            await refetchSchedules()
            closeAllModals()
          },
        },
      )
    } else {
      launchStrategyMutation(
        {
          content_strategy_type: strategy.id,
          length: newStrategy.length,
          seo_contents: newStrategy.seo_contents,
          email_contents: newStrategy.email_contents,
          instagram_contents: newStrategy.instagram_contents,
          crosspost_contents: newStrategy.crosspost_contents,
          video_crosspost_contents: newStrategy.video_crosspost_contents,
          tactics: newStrategy.tactics,
        },
        {
          onSuccess: async () => {
            await refetchBrandPlan()
            await refetchSchedules()
            closeAllModals()
          },
        },
      )
    }
  }

  if (useContentTypes) {
    return (
      <Modal.Root size="md">
        <Modal.Header title="Set the default content types for campaigns in your strategy" />
        <Modal.Content fillAvailableSpace>
          <ContentTypeGrid
            counts={counts}
            onCountChange={handleCountChange}
            isStrictBrandControl={isStrictBrandControl}
          />
        </Modal.Content>
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Modal.FooterButton onPress={closeModal} variant="tertiary">
              Cancel
            </Modal.FooterButton>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            <div className={Styles.footerRight}>
              <Text variant="metadata" color="var(--dark-90)">
                {credits.creditsNeeded} credits per campaign
              </Text>
              <Modal.FooterButton onPress={launchStrategy} isDisabled={isLaunching}>
                Add Campaigns Now
              </Modal.FooterButton>
            </div>
          </Modal.FooterContent>
        </Modal.Footer>
      </Modal.Root>
    )
  }

  return (
    <Modal.Root size="md" bodyClassName={Styles.modalBody}>
      <Modal.Header title="Customize your strategy" />
      <Modal.Content>
        <CustomizeTable
          key={newStrategy.id}
          strategy={newStrategy}
          onUpdate={(updatedStrategy) => {
            setNewStrategy(updatedStrategy)
          }}
          selectedBlogPlatform={selectedBlogPlatform}
          setSelectedBlogPlatform={setSelectedBlogPlatform}
          selectedEmailPlatform={selectedEmailPlatform}
          setSelectedEmailPlatform={setSelectedEmailPlatform}
        />
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} variant="tertiary">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterButton onPress={launchStrategy} isDisabled={isLaunching}>
          <div>
            <span>Launch Now</span>
            <Credits size={15} color="var(--light-60)" />
            {credits.creditsNeeded}
          </div>
        </Modal.FooterButton>
      </Modal.Footer>
    </Modal.Root>
  )
}
